import { createRoute, redirect, type AnyRoute } from '@tanstack/react-router';
import { CalendarioPage } from '@/features/changes/CalendarioPage';
import { CongelamentosPage } from '@/features/changes/CongelamentosPage';
import { JanelasPage } from '@/features/changes/JanelasPage';
import { MudancasLayout } from '@/features/changes/MudancasLayout';
import { CAMINHOS, RAIZ_DE_MUDANCAS } from '@/features/changes/navegacao';

/**
 * Rotas do calendário de mudanças.
 *
 * Exportadas como **fábrica**, pelo mesmo motivo das outras features:
 * `createRoute` exige a rota pai, e o pai (`appRoute`, com a casca e a guarda
 * de sessão) mora em `app/router.tsx`. Receber o pai por parâmetro deixa a
 * integração ser uma linha lá, sem que esta feature importe de dentro de
 * `app/` — o que fecharia um ciclo entre o roteador e a tela que ele monta.
 *
 * Volta **uma rota só**, com as abas já penduradas: quantas abas o calendário
 * tem é decisão da feature.
 *
 * A **ficha da mudança** não está aqui. Ela é uma aba do chamado, porque a
 * mudança é extensão 1:1 do chamado (ADR-001): dar a ela um endereço próprio
 * criaria duas páginas para o mesmo registro, uma com comentários e anexos e
 * outra com planos e risco — e nenhuma das duas seria "o chamado".
 */
export function criarRotasDeMudancas<TRotaPai extends AnyRoute>(rotaPai: TRotaPai) {
  const raiz = createRoute({
    getParentRoute: () => rotaPai,
    path: RAIZ_DE_MUDANCAS,
    component: MudancasLayout,
  });

  /**
   * `/mudancas` sozinho não é tela: redireciona para o calendário.
   *
   * Repetir o calendário no índice daria dois endereços para a mesma tela, e o
   * item da navegação principal acenderia sem que nenhuma aba ficasse marcada.
   */
  const indice = createRoute({
    getParentRoute: () => raiz,
    path: '/',
    // `href` e não `to`: este módulo é importado por `app/router.tsx`, então os
    // caminhos daqui ainda não existem no tipo do roteador quando ele é
    // compilado. Mesma razão de `features/admin/routes.ts`.
    beforeLoad: () => {
      throw redirect({ href: `${RAIZ_DE_MUDANCAS}/${CAMINHOS.calendario}` });
    },
  });

  const aba = (caminho: string, component: () => JSX.Element) =>
    createRoute({ getParentRoute: () => raiz, path: caminho, component });

  return raiz.addChildren([
    indice,
    aba(CAMINHOS.calendario, CalendarioPage),
    aba(CAMINHOS.janelas, JanelasPage),
    aba(CAMINHOS.congelamentos, CongelamentosPage),
  ]);
}

export { CAMINHOS, RAIZ_DE_MUDANCAS };
